import type { IComponentController, IScope } from 'angular';
import template from './mx-adaptive-cards-container.component.html';
import { AdaptiveCardsService, AdaptiveCardInstance } from '../mx-adaptive-cards.service';
import { DashboardWidgetComponent, DashboardWidgetController } from '../dashboard-widget/dashboard-widget.component';
import { DashboardConfigurationMode } from '../dashboard-page/dashboard-page.component';
import type { EventService } from '../../App/services/event-service';
import { EventCodes } from '../../App/model/event-codes';

export class AdaptiveCardsContainerController implements IComponentController {
	public embeddedMode = false;
	public cards: AdaptiveCardInstance[] = [];
	public loading = false;
	public expanded = false;

	// Only the first row (the wide first card + one narrow card) shows by default; anything past
	// that is behind the "Show more" toggle.
	private static readonly collapsedCardsCount = 2;

	public get visibleCards(): AdaptiveCardInstance[] {
		return this.expanded ? this.cards : this.cards.slice(0, AdaptiveCardsContainerController.collapsedCardsCount);
	}

	public get hasMoreCards(): boolean {
		return this.cards.length > AdaptiveCardsContainerController.collapsedCardsCount;
	}

	// Populated via `require` only when we're rendered as a dashboard widget's content (not on
	// the standalone Dashboard page) — see dashboard-widget.component.ts's handleAutoHeight().
	public dashboardWidget?: DashboardWidgetController;

	static readonly $inject = ['$scope', 'mx.sb.Dashboard.AdaptiveCardsService', 'mx.shell.EventService'];

	constructor(
		private readonly $scope: IScope,
		private readonly adaptiveCardsService: AdaptiveCardsService,
		private readonly eventService: EventService
	) {}

	async $onInit() {
		// Re-evaluate when leaving edit mode: while editing, an empty container is kept mounted
		// (see collapseWidgetIfEmpty) so it stays visible/movable; once editing ends it should
		// collapse again if it's still empty.
		this.eventService.subscribeTo(EventCodes.DashboardPageConfigurationModeChanged, () => this.collapseWidgetIfEmpty(), this.$scope);

		this.loading = true;
		try {
			this.cards = await this.adaptiveCardsService.getAdaptiveCards();
			this.collapseWidgetIfEmpty();
		} finally {
			this.loading = false;
		}

		//this.enableWidgetAutoHeight();

		// this.cards = await this.adaptiveCardsService.getAdaptiveCards();
		// this.collapseWidgetIfEmpty();
	}

	public toggleExpanded() {
		this.expanded = !this.expanded;
	}

	public async hideCard(card: AdaptiveCardInstance) {
		await this.adaptiveCardsService.hideAdaptiveCards([card.objectId]);
		const index = this.cards.indexOf(card);
		if (index >= 0) {
			this.cards.splice(index, 1);
		}
		this.collapseWidgetIfEmpty();
	}

	// GridStack can't represent a 0-row item — AutoHeight only ever shrinks down to ~1 row, since
	// it measures the widget's actual rendered height (header/status bar included) and rounds up.
	// So once there's nothing left to show, reclaim the space by pulling the widget out of the grid
	// entirely instead — see DashboardPageController.collapseWidgetInstance for why this doesn't
	// persist (cards could reappear, and a fresh load would show the widget normally again).
	//
	// While the dashboard is in edit mode, skip this: the user needs to see and reposition the
	// (empty) widget like any other, so DashboardPageController.enableConfigurationMode() restores
	// it if it was already collapsed, and we leave it mounted here until editing ends.
	private collapseWidgetIfEmpty() {
		if (this.cards.length || !this.dashboardWidget) {
			return;
		}
		if (this.dashboardWidget.page.configurationMode !== DashboardConfigurationMode.Disabled) {
			return;
		}
		this.dashboardWidget.page.collapseWidgetInstance(this.dashboardWidget.widgetInstance);
	}

	// Turns on the same "shrink/grow the gridstack item to fit its content" behavior a user could
	// toggle manually for any dashboard widget, so the widget collapses instead of leaving an
	// empty gap once all its cards are hidden.
	private enableWidgetAutoHeight() {
		const widgetConfiguration = this.dashboardWidget?.widgetInstance?.dataModel?.WidgetConfiguration;
		if (widgetConfiguration && !widgetConfiguration.AutoHeight) {
			widgetConfiguration.AutoHeight = true;
		}
	}
}

export const AdaptiveCardsContainerComponent = {
	name: 'mxAdaptiveCardsContainer',
	controller: AdaptiveCardsContainerController,
	controllerAs: 'vm',
	template,
	bindings: {
		embeddedMode: '<',
	},
	require: {
		dashboardWidget: `?^^${DashboardWidgetComponent.name}`,
	},
};
